import { z } from "zod";
import { localizedTextSchema } from "./intake-template";
import { JOURNEY_STEP_KEYS, type JourneyStepKey } from "./journey-steps";
import { TREATMENT_PHASES, type TreatmentPhase } from "./patient-journey-state";

// TES-359 — authorable veckotema + "För dig idag". The hardcoded THEMES table
// in home-summary.ts becomes the FALLBACK: a tenant may author one WeeklyTheme
// row per derived treatment phase (MVP stays phase-keyed per product decision),
// and /home-summary serves the PUBLISHED row when present, else the code table.
// One row drives BOTH §1 (greeting theme) and §5 ("För dig idag"), preserving
// the shared-focus invariant structurally — title/body feed §1, forYouTitle/
// forYouBody feed §5, all on the same phase key.

// TES-420 — a theme row's identity. Either a bare treatment phase (the
// baseline that serves the whole phase) or a phase SCOPED TO ONE JOURNEY STEP,
// e.g. "pre_treatment.intake".
//
// Why: `deriveTreatmentPhase` reports `pre_treatment` from signup until the
// first prescription, so a phase-keyed model can only ever show ONE greeting
// and ONE "För dig idag" across the entire onboarding ladder — while
// "Dina steg" and "Vad händer just nu" advance step by step. That mismatch is
// the desync that was reported. Scoping the key to a journey step gives §1/§5
// the same axis the rest of Home already uses.
//
// Resolution is most-specific-first (see WeeklyThemeService.resolvePublished):
// a step row shadows its phase baseline, and the baseline still covers every
// step nobody authored — so partial authoring degrades to today's behaviour
// instead of a blank Home.
export function isThemeScopeKey(v: string): boolean {
  const [phase, step, ...rest] = v.split(".");
  if (rest.length > 0) return false;
  if (!TREATMENT_PHASES.includes(phase as TreatmentPhase)) return false;
  return (
    step === undefined || JOURNEY_STEP_KEYS.includes(step as JourneyStepKey)
  );
}

export const themeScopeKeySchema = z.string().refine(isThemeScopeKey, {
  message:
    "phaseKey must be a treatment phase, optionally scoped to a journey step (e.g. pre_treatment.intake)",
});

// Build the resolution candidates for a patient, most specific first.
export function themeScopeCandidates(
  phase: TreatmentPhase,
  stepKey: JourneyStepKey | null,
): string[] {
  return stepKey ? [`${phase}.${stepKey}`, phase] : [phase];
}

export const WEEKLY_THEME_STATUSES = ["draft", "published"] as const;
export const weeklyThemeStatusSchema = z.enum(WEEKLY_THEME_STATUSES);
export type WeeklyThemeStatus = z.infer<typeof weeklyThemeStatusSchema>;

// Both locales required + non-empty: a published theme must never render blank
// in either language (same rule as upsertWellnessFocusSchema).
const nonEmptyLocalizedSchema = z.object({
  sv: z.string().min(1),
  en: z.string().min(1),
});

// Admin read DTO — the full stored theme for one phase.
export const weeklyThemeDtoSchema = z.object({
  id: z.string().uuid(),
  // TES-420 — a bare phase, or a phase scoped to one journey step.
  phaseKey: themeScopeKeySchema,
  title: localizedTextSchema,
  body: localizedTextSchema,
  forYouTitle: localizedTextSchema,
  forYouBody: localizedTextSchema,
  // §5 CTA — ctaLabel is stored for admin/authoring completeness; the mobile
  // veckotema contract carries only ctaPath today (the app renders its own
  // label). Both-or-neither, enforced on write.
  ctaLabel: localizedTextSchema.nullable(),
  ctaPath: z.string().nullable(),
  status: weeklyThemeStatusSchema,
});
export type WeeklyThemeDto = z.infer<typeof weeklyThemeDtoSchema>;

// The admin list view: every derived treatment phase, with the authored row or
// null (phases without a row fall back to the code table in /home-summary).
export const weeklyThemeListEntrySchema = z.object({
  phaseKey: themeScopeKeySchema,
  theme: weeklyThemeDtoSchema.nullable(),
});
export type WeeklyThemeListEntry = z.infer<typeof weeklyThemeListEntrySchema>;

// Create/update payload (admin + bulk import). `phaseKey` is the identity —
// one row per (tenant, phase).
export const weeklyThemeUpsertSchema = z
  .object({
    phaseKey: themeScopeKeySchema,
    title: nonEmptyLocalizedSchema,
    body: nonEmptyLocalizedSchema,
    forYouTitle: nonEmptyLocalizedSchema,
    forYouBody: nonEmptyLocalizedSchema,
    // CTA: either both label + path or neither.
    ctaLabel: nonEmptyLocalizedSchema.nullable().optional(),
    ctaPath: z.string().min(1).max(200).nullable().optional(),
    status: weeklyThemeStatusSchema.default("draft"),
  })
  .superRefine((v, ctx) => {
    const hasLabel = v.ctaLabel != null;
    const hasPath = v.ctaPath != null;
    if (hasLabel !== hasPath) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [hasLabel ? "ctaPath" : "ctaLabel"],
        message: "ctaLabel and ctaPath must be set together",
      });
    }
  });
export type WeeklyThemeUpsert = z.infer<typeof weeklyThemeUpsertSchema>;
